(() => {
  'use strict';

  if (window.AoVnextPdp && typeof window.AoVnextPdp.scan === 'function') {
    window.AoVnextPdp.scan(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const closestFrom = (target, selector) => {
    if (target instanceof Element) return target.closest(selector);
    return target?.parentElement?.closest(selector) || null;
  };

  const createInstance = (root) => {
    if (!root || instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];

    const listen = (target, eventName, handler, options = {}) => {
      target?.addEventListener(eventName, handler, { ...options, signal });
    };

    if (root.matches('[data-aocdx-root].ao-vnext-purchase')) {
      const plans = [...root.querySelectorAll('[data-aocdx-plan]')];
      const currentSavings = root.querySelector('[data-ao-vnext-current-save]');
      const renderCurrentSavings = () => {
        if (!currentSavings) return;
        const activePlan = plans.find((plan) => plan.getAttribute('aria-checked') === 'true');
        const text = activePlan?.dataset.aoVnextSavingText?.trim() || '';
        currentSavings.textContent = text;
        currentSavings.hidden = !text;
      };
      const planObserver = new MutationObserver(renderCurrentSavings);
      plans.forEach((plan) => planObserver.observe(plan, {
        attributes: true,
        attributeFilter: ['aria-checked']
      }));
      cleanups.push(() => planObserver.disconnect());
      renderCurrentSavings();
    }

    if (root.matches('[data-ao-vnext-compare]')) {
      const range = root.querySelector('[data-ao-vnext-compare-range]');
      const stage = root.querySelector('.ao-vnext-compare__stage');
      const output = root.querySelector('[data-ao-vnext-compare-value]');
      const beforeLabel = root.querySelector('.ao-vnext-compare__label--before')?.textContent?.trim() || 'before';
      const afterLabel = root.querySelector('.ao-vnext-compare__label--after')?.textContent?.trim() || 'after';
      const render = () => {
        const nextValue = Number(range?.value);
        const value = Number.isFinite(nextValue) ? Math.max(0, Math.min(100, nextValue)) : 50;
        root.style.setProperty('--ao-vnext-compare-position', `${value}%`);
        if (output) {
          output.value = `${value}%`;
          output.textContent = `${value}%`;
        }
        range?.setAttribute('aria-valuetext', `${value}% ${beforeLabel}, ${100 - value}% ${afterLabel}`);
      };
      listen(range, 'input', render, { passive: true });
      listen(range, 'change', render, { passive: true });

      /* O-11 touch-intent gate (port of the Home results controller API 3, assets/ao-home-v3-results.js).
         With Pointer Events the root carries [data-ao-vnext-compare-touch]; ao-vnext-pdp.css then sets pointer-events: none on
         the native range, which keeps keyboard and assistive-technology input only. The stage decides per pointer:
         · mouse, primary button: jump to x, capture, drag (and focus the range for the keyboard);
         · touch, pen and unknown types: nothing moves on pointerdown. After more than 8 px of travel the gesture drags only on
           horizontal intent (|dx| > 1.2 × |dy|); otherwise it is dropped and the page scrolls (touch-action: pan-y). A tap
           changes nothing; pointercancel / lostpointercapture end the gesture.
         Position = x inside the range box (the stage padding box, the same box the clip and the divider use), no thumb offset.
         Without Pointer Events the attribute is not set and the native range keeps its own input. */
      if (range && stage && typeof window.PointerEvent === 'function') {
        const INTENT_PX = 8;
        const HORIZONTAL = 1.2;
        let gesture = null;
        root.setAttribute('data-ao-vnext-compare-touch', '');
        cleanups.push(() => root.removeAttribute('data-ao-vnext-compare-touch'));

        const follow = (event) => {
          let box = range.getBoundingClientRect();
          if (!box.width) box = stage.getBoundingClientRect();
          if (!box.width) return;
          const next = String(Math.max(0, Math.min(100, Math.round(((event.clientX - box.left) / box.width) * 100))));
          if (range.value === next) return;
          range.value = next;
          render();
        };
        const capture = (event) => {
          try { stage.setPointerCapture(event.pointerId); } catch (_error) { /* capture is optional */ }
        };
        const release = (event) => {
          if (gesture && event.pointerId === gesture.id) gesture = null;
        };

        listen(stage, 'pointerdown', (event) => {
          if (!event.isPrimary) return;
          if (event.pointerType === 'mouse') {
            if (event.button !== 0) return;
            event.preventDefault();
            gesture = { id: event.pointerId, drag: true };
            capture(event);
            range.focus({ preventScroll: true });
            follow(event);
            return;
          }
          // Touch, pen and unknown pointer types: record only. Nothing moves until the gesture shows horizontal intent.
          gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, drag: false };
        });

        listen(stage, 'pointermove', (event) => {
          if (!gesture || event.pointerId !== gesture.id) return;
          if (!gesture.drag) {
            const dx = Math.abs(event.clientX - gesture.x);
            const dy = Math.abs(event.clientY - gesture.y);
            if (dx <= INTENT_PX && dy <= INTENT_PX) return;
            if (dx <= INTENT_PX || dx <= HORIZONTAL * dy) {
              gesture = null; // vertical or diagonal intent: the page scrolls natively
              return;
            }
            gesture.drag = true;
            capture(event);
          }
          if (event.cancelable) event.preventDefault();
          follow(event);
        });

        listen(stage, 'pointerup', release);
        listen(stage, 'pointercancel', release);
        // lostpointercapture bubbles: a child losing its implicit capture must not end the stage gesture.
        listen(stage, 'lostpointercapture', (event) => {
          if (event.target === stage) release(event);
        });
        // A mouse drag must never start a native image drag (which would cancel the pointer stream).
        listen(stage, 'dragstart', (event) => event.preventDefault());
      }
      render();
    }

    if (root.matches('[data-ao-vnext-ritual]')) {
      const tabs = [...root.querySelectorAll('[data-ao-vnext-ritual-tab]')];
      const panels = [...root.querySelectorAll('[data-ao-vnext-ritual-panel]')];
      const descriptions = [...root.querySelectorAll('[data-ao-vnext-ritual-description]')];
      const activate = (key, focus = false) => {
        const activeTab = tabs.find((tab) => tab.dataset.aoVnextRitualTab === key);
        const activePanel = panels.find((panel) => panel.dataset.aoVnextRitualPanel === key);
        if (!activeTab || !activePanel) return;
        tabs.forEach((tab) => {
          const active = tab === activeTab;
          tab.classList.toggle('ao-vnext-ritual__tab--active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel === activePanel;
          panel.classList.toggle('ao-vnext-ritual__panel--active', active);
          panel.hidden = !active;
        });
        descriptions.forEach((description) => {
          description.hidden = description.dataset.aoVnextRitualDescription !== key;
        });
        if (focus) activeTab.focus();
      };
      tabs.forEach((tab, index) => {
        listen(tab, 'click', () => activate(tab.dataset.aoVnextRitualTab));
        listen(tab, 'keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          else next = (index + 1) % tabs.length;
          activate(tabs[next].dataset.aoVnextRitualTab, true);
        });
      });
      const initialTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
      if (initialTab) activate(initialTab.dataset.aoVnextRitualTab);
    }

    if (root.matches('[data-ao-vnext-reviews]')) {
      const filters = [...root.querySelectorAll('[data-ao-vnext-review-filter]')];
      const cards = [...root.querySelectorAll('[data-ao-vnext-review-card]')];
      const loadButton = root.querySelector('button[data-ao-vnext-review-load]');
      const status = root.querySelector('[data-ao-vnext-review-status]');
      const readCount = (value, fallback) => {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
      };
      // Native "load more" is active only when Liquid rendered the reveal button (no destination URL, no review app).
      const initialLimit = loadButton ? readCount(root.dataset.aoVnextReviewsInitial, 0) : 0;
      const step = readCount(root.dataset.aoVnextReviewsStep, 4);
      const statusTemplate = status?.dataset.aoVnextReviewStatusTemplate || 'Showing [shown] of [total] reviews';
      let limit = initialLimit > 0 ? initialLimit : Infinity;
      let activeKey = 'all';

      const normalize = (value) => String(value || '')
        .toLowerCase()
        .split(/[\s,|]+/)
        .map((part) => part.trim())
        .filter(Boolean);
      const matchesKey = (card, key) => key === 'all' || normalize(card.dataset.aoVnextReviewGroups).includes(key);
      const matching = () => cards.filter((card) => matchesKey(card, activeKey));

      // Show the cards of the active filter up to the current limit; returns the cards that became visible.
      const render = ({ announce = false } = {}) => {
        const list = matching();
        const revealed = [];
        cards.forEach((card) => {
          const rank = list.indexOf(card);
          const show = rank >= 0 && rank < limit;
          if (show && card.hidden) revealed.push(card);
          card.hidden = !show;
          card.setAttribute('aria-hidden', String(!show));
        });
        const shown = Math.min(limit, list.length);
        if (loadButton) loadButton.hidden = shown >= list.length;
        if (announce && status) {
          status.textContent = statusTemplate
            .replace('[shown]', String(shown))
            .replace('[total]', String(list.length));
        }
        return revealed;
      };

      const filterCards = (requestedKey) => {
        activeKey = normalize(requestedKey)[0] || 'all';
        limit = initialLimit > 0 ? initialLimit : Infinity;
        filters.forEach((filter) => {
          const active = (normalize(filter.dataset.aoVnextReviewFilter)[0] || 'all') === activeKey;
          filter.classList.toggle('ao-vnext-reviews__filter--active', active);
          filter.setAttribute('aria-pressed', String(active));
        });
        render({ announce: true });
      };

      filters.forEach((filter) => {
        listen(filter, 'click', () => filterCards(filter.dataset.aoVnextReviewFilter || 'all'));
      });
      root.querySelectorAll('[data-ao-vnext-review-helpful]').forEach((button) => {
        if (!button.hasAttribute('aria-pressed')) button.setAttribute('aria-pressed', 'false');
        listen(button, 'click', () => {
          const group = button.closest('.ao-vnext-reviews__helpful');
          group?.querySelectorAll('[data-ao-vnext-review-helpful]').forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });
        });
      });

      // Every review action (button or link) announces itself; a button hands off to an installed review app.
      root.querySelectorAll('[data-ao-vnext-review-action]').forEach((control) => {
        listen(control, 'click', () => {
          const action = control.dataset.aoVnextReviewAction;
          root.dispatchEvent(new CustomEvent('ao-vnext:review-action', {
            bubbles: true,
            detail: { action, href: control.getAttribute('href') || null }
          }));
          if (control.tagName !== 'BUTTON' || control.hasAttribute('data-ao-vnext-review-load')) return;
          const app = root.querySelector('[data-ao-vnext-review-app]');
          if (app) {
            app.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
            app.querySelector('button, a, input, select, textarea')?.focus({ preventScroll: true });
          }
        });
      });

      if (loadButton) {
        listen(loadButton, 'click', () => {
          limit = (Number.isFinite(limit) ? limit : 0) + step;
          const revealed = render({ announce: true });
          const first = revealed[0];
          if (!first) return;
          // Keep keyboard users in place: focus moves to the first card that just appeared.
          if (!first.hasAttribute('tabindex')) {
            first.setAttribute('tabindex', '-1');
            listen(first, 'blur', () => first.removeAttribute('tabindex'), { once: true });
          }
          first.focus({ preventScroll: true });
          first.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'nearest' });
        });
        render();
      }

      // Theme editor: selecting a card hidden by a filter or by the "load more" limit reveals it.
      root.aoVnextRevealReview = (card) => {
        if (!cards.includes(card)) return;
        if (!matchesKey(card, activeKey)) filterCards('all');
        const rank = matching().indexOf(card);
        if (rank >= limit) {
          limit = rank + 1;
          render();
        }
      };
      cleanups.push(() => {
        delete root.aoVnextRevealReview;
      });
    }

    if (root.matches('[data-ao-vnext-details]')) {
      root.querySelectorAll('[data-ao-vnext-faq-button]').forEach((button) => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        listen(button, 'click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          if (panel) panel.hidden = expanded;
        });
      });
    }

    instances.set(root, {
      destroy() {
        controller.abort();
        cleanups.forEach((cleanup) => cleanup());
        instances.delete(root);
      }
    });
  };

  const scan = (scope = document) => {
    const selector = [
      '[data-aocdx-root].ao-vnext-purchase',
      '[data-ao-vnext-compare]',
      '[data-ao-vnext-ritual]',
      '[data-ao-vnext-reviews]',
      '[data-ao-vnext-details]'
    ].join(',');
    if (scope.matches?.(selector)) createInstance(scope);
    scope.querySelectorAll?.(selector).forEach(createInstance);
  };

  const destroyWithin = (scope) => {
    if (!scope) return;
    const descendants = scope.querySelectorAll
      ? [...scope.querySelectorAll('[data-aocdx-root].ao-vnext-purchase, [data-ao-vnext-compare], [data-ao-vnext-ritual], [data-ao-vnext-reviews], [data-ao-vnext-details]')]
      : [];
    const roots = [scope, ...descendants];
    roots.forEach((root) => instances.get(root)?.destroy());
  };

  const selectPlan = (key) => {
    const purchase = document.querySelector('[data-aocdx-root].ao-vnext-purchase');
    if (!purchase) return false;
    const number = Number(key);
    if (![1, 2, 3].includes(number)) return false;
    if (typeof purchase.aocdxSelectPlan === 'function') {
      purchase.aocdxSelectPlan(number);
      return true;
    }
    const plan = purchase.querySelector(`[data-aocdx-plan="${number}"]`);
    if (!plan || plan.disabled || plan.getAttribute('aria-disabled') === 'true') return false;
    plan.click();
    return true;
  };

  const globalController = new AbortController();
  const globalSignal = globalController.signal;

  document.addEventListener('click', (event) => {
    const finalSubmit = closestFrom(event.target, '[data-ao-vnext-final-submit]');
    if (finalSubmit) {
      event.preventDefault();
      if (!selectPlan(finalSubmit.dataset.aoVnextPlanProxy || 1)) return;
      const submit = document.querySelector('.ao-vnext-purchase [data-aocdx-submit]:not(.ao-vnext-purchase__sticky-button)');
      if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') submit.click();
      return;
    }

    const proxy = closestFrom(event.target, '[data-ao-vnext-plan-proxy]');
    if (!proxy) return;
    event.preventDefault();
    if (selectPlan(proxy.dataset.aoVnextPlanProxy || 1)) {
      const buy = document.getElementById('buy');
      buy?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }, { signal: globalSignal });

  document.addEventListener('shopify:section:load', (event) => scan(event.target), { signal: globalSignal });
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target), { signal: globalSignal });
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return;
    const ritualPanel = target.closest('[data-ao-vnext-ritual-panel]');
    const ritual = ritualPanel?.closest('[data-ao-vnext-ritual]');
    if (ritualPanel && ritual) {
      ritual.querySelector(`[data-ao-vnext-ritual-tab="${CSS.escape(ritualPanel.dataset.aoVnextRitualPanel)}"]`)?.click();
    }
    const reviewCard = target.closest?.('[data-ao-vnext-review-card]');
    if (reviewCard?.hidden) {
      const reviews = reviewCard.closest('[data-ao-vnext-reviews]');
      if (typeof reviews?.aoVnextRevealReview === 'function') reviews.aoVnextRevealReview(reviewCard);
      else reviews?.querySelector('[data-ao-vnext-review-filter="all"]')?.click();
    }
    const faqItem = target.closest('.ao-vnext-details__faq-item');
    const faqButton = faqItem?.querySelector('[data-ao-vnext-faq-button]');
    const faqPanel = faqButton ? document.getElementById(faqButton.getAttribute('aria-controls')) : null;
    if (faqItem && faqButton && faqPanel) {
      if (faqItem.dataset.aoEditorWasExpanded == null) faqItem.dataset.aoEditorWasExpanded = String(faqButton.getAttribute('aria-expanded') === 'true');
      faqButton.setAttribute('aria-expanded', 'true');
      faqPanel.hidden = false;
    }
  }, { signal: globalSignal });
  document.addEventListener('shopify:block:deselect', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const faqItem = target?.closest?.('.ao-vnext-details__faq-item');
    if (!faqItem || faqItem.dataset.aoEditorWasExpanded == null) return;
    const faqButton = faqItem.querySelector('[data-ao-vnext-faq-button]');
    const faqPanel = faqButton ? document.getElementById(faqButton.getAttribute('aria-controls')) : null;
    const expanded = faqItem.dataset.aoEditorWasExpanded === 'true';
    faqButton?.setAttribute('aria-expanded', String(expanded));
    if (faqPanel) faqPanel.hidden = !expanded;
    delete faqItem.dataset.aoEditorWasExpanded;
  }, { signal: globalSignal });

  window.AoVnextPdp = Object.freeze({ version: '1.2.0', scan, destroyWithin });
  scan(document);
})();
